import {skipToken} from '@reduxjs/toolkit/query';
import {OptionStatusIndicator} from 'platform/components';

import {isNil, isNotNil} from 'ramda';

import {
  useGetWarrantyProgramQuery,
  useListWarrantyProgramQuery,
  WarrantyProgramListItemResponseBody,
  WarrantyProgramResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

type WarrantyProgramOption = {
  value: string;
  label: string;
  statusIndicator?: OptionStatusIndicator;
};

export function useWarrantyProgramOptions(selectedWarrantyProgramId: string | Nullish) {
  const {data: warrantyPrograms, isLoading: isProgramListLoading} = useListWarrantyProgramQuery();

  const {data: removedWarrantyProgram, isLoading: isRemovedProgramLoading} =
    useGetWarrantyProgramQuery(
      isNotNil(selectedWarrantyProgramId) &&
        isNotNil(warrantyPrograms) &&
        warrantyPrograms.every((program) => program.id !== selectedWarrantyProgramId)
        ? {warrantyProgramId: selectedWarrantyProgramId}
        : skipToken
    );

  return {
    warrantyProgramOptions: buildWarrantyProgramOptions(warrantyPrograms, removedWarrantyProgram),
    isLoading: isProgramListLoading || isRemovedProgramLoading,
  };
}

function buildWarrantyProgramOptions(
  activePrograms: Pick<WarrantyProgramListItemResponseBody, 'id' | 'name'>[] | Nullish,
  selectedProgram: Pick<WarrantyProgramResponseBody, 'id' | 'name' | 'active'> | Nullish
): WarrantyProgramOption[] {
  if (isNil(activePrograms)) {
    return [];
  }

  const activeOptions = activePrograms.map(({id, name}) => ({value: id, label: name}));

  return isNotNil(selectedProgram) &&
    activePrograms.every((program) => program.id !== selectedProgram.id)
    ? [
        ...activeOptions,
        {
          value: selectedProgram.id,
          label: selectedProgram.name,
          statusIndicator:
            selectedProgram.active === false
              ? {tooltip: i18n.t('entity.warranty.labels.warrantyProgramRemoved')}
              : undefined,
        },
      ]
    : activeOptions;
}
